'use client';
// Demandes de nouveaux documents (design isReq) — cartes + décision RQ + fil
// de discussion. L'assistant guidé vit désormais dans une fenêtre superposée
// (lot GED-2, cf. `AssistantDemande`) : il s'ouvre aussi depuis le catalogue.
import React, { useMemo, useState } from 'react';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed, userName } from '@/lib/store';
import { fmtDate, PERIODICITES, estAffectable } from '@/lib/meta';
import { BoutonCreer, TitrePage, I, card, inputStyle, StatePill, FieldError, useBusy, Selecteur, useConfirmation, ChampRecherche} from '@/components/ui';
import { AssistantDemande } from '@/components/AssistantDemande';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import type { GedDemandeProps } from '@/lib/types';
import { fetchEcriture } from '@/lib/ecrire';

import { Segment } from '@/shared/Segment';
type AssignDraft = {
  redacteurs: string[]; verificateur: string; approbateur: string; periodicite: string; motif: string;
  /* Métadonnées CORRIGEABLES à l'approbation (14/08/2026). Elles partent au
     serveur telles quelles : une valeur vidée volontairement est un choix,
     pas une absence de saisie. */
  code: string; titre: string; type: string; processus: string; discipline: string; site: string; perimetre: string;
  /* Les DEUX AXES du périmètre — fonctions (le métier) et postes (ce qui est
     tenu au planning), séparés le 06/09/2026. `perimetre` reste porté pour
     afficher la phrase des demandes déposées avant ce changement ; le serveur
     la RECALCULE dès qu'une case est cochée sur l'un ou l'autre axe. */
  perimetrePostes: string[];
  perimetreFonctions: string[];
};

/* ---------------------------------------------------------------------------
 * LES QUATRE ÉTATS D'UNE DEMANDE (lot GED-1)
 *
 * L'écran n'avait que « À statuer » et « Archives ». Deux conséquences :
 *   — une demande renvoyée à discussion restait mêlée à celles que personne
 *     n'avait encore regardées, distinguée par une simple pastille. Or ce n'est
 *     pas la même attente : l'une attend une décision du RQ, l'autre attend une
 *     RÉPONSE au point à cadrer. Les confondre, c'est perdre les secondes ;
 *   — « Archives » recevait aussi bien une demande refusée qu'une demande
 *     approuvée dont le document est encore en cours de rédaction. Archiver ce
 *     qui n'est pas terminé, c'est cesser de le suivre.
 *
 * Les quatre onglets ci-dessous sont des états RÉELS, pas un découpage
 * cosmétique de la même liste :
 *   · statuer   — `decision` absente : aucune décision prise ;
 *   · discutees — `decision === 'discuter'`, valeur écrite par le serveur
 *                 (`decideDemande`). L'écran ne la pose plus depuis le
 *                 19/10/2026 (deux décisions : refuser, approuver) ; les
 *                 demandes déjà renvoyées restent ici, à statuer ;
 *   · enrevue   — demande approuvée dont le document issu est ENCORE EN CIRCUIT
 *                 (brouillon → à vérifier → à approuver). L'état vient du
 *                 document, pas de la demande : c'est la suite donnée, et elle
 *                 était déjà calculée plus bas pour la ligne « Avancement » ;
 *   · archives  — le reste : refusée, ou approuvée dont le document est entré en
 *                 service — ou reste introuvable dans le fonds.
 *
 * Un document `enrevision` est DÉJÀ en vigueur : la demande qui l'a fait naître
 * est honorée, elle part aux archives.
 */
type RoleCircuit = 'redacteur' | 'verificateur' | 'approbateur';
type Elig = { id: string; ok: boolean; raison?: string; derogation?: boolean };
/** Le verdict du moteur sur le circuit proposé, pour UNE version du choix (`cle`). */
type Jugement = { cle: string; etat: 'ok' | 'echec'; erreurs: string[]; eligibles: Record<RoleCircuit, Elig[]> | null };

type Onglet = 'statuer' | 'discutees' | 'enrevue' | 'archives';
const EN_CIRCUIT = ['brouillon', 'averifier', 'aapprouver'];

/* Libellé, ordre et INFOBULLE de chaque onglet. L'infobulle n'est pas un
   ornement : « en revue » et « à discuter » sont deux attentes différentes, et
   rien à l'écran ne disait laquelle. */
const ONGLETS: readonly (readonly [Onglet, string, string])[] = [
  ['statuer', 'À statuer', 'Aucune décision prise — le responsable qualité doit trancher.'],
  ['discutees', 'Discutées', 'Renvoyées à discussion : un point reste à cadrer avant de trancher.'],
  ['enrevue', 'En revue', 'Approuvées, document en cours de rédaction ou de vérification.'],
  ['archives', 'Archives', 'Terminées : refusées, ou document entré en service.'],
] as const;

const VIDE: Record<Onglet, string> = {
  statuer: 'Aucune demande en attente de décision.',
  discutees: 'Aucune demande renvoyée à discussion.',
  enrevue: 'Aucune demande approuvée dont le document soit encore en circuit.',
  archives: 'Aucune demande terminée pour le moment.',
};

const DECISIONS: Record<string, { label: string; bg: string; fg: string }> = {
  approuvee: { label: 'Approuvée', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  approuvee_mod: { label: 'Approuvée avec modif.', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  discuter: { label: 'À discuter', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  refusee: { label: 'Refusée', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

export function Demandes() {
  const { demandes, docs, settings, session, decideDemande, addDemandeNote, openDoc, reopenDemande } = useGed();
  const isRQ = session.roles.includes('rq');
  const canWrite = session.roles.includes('redacteur') || isRQ;
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  // Les demandes traitées quittent la pile de travail sans quitter le dossier :
  // elles restent consultables, avec leur décision, leur fil de discussion et
  // l'avancement du document qui en est issu (ISO 15189 §8.3 — la traçabilité
  // d'une demande ne s'arrête pas à la décision). Quatre onglets depuis GED-1,
  // cf. le bloc « LES QUATRE ÉTATS D'UNE DEMANDE » en tête de fichier.
  const [onglet, setOnglet] = useState<Onglet>('statuer');
  // Fenêtre superposée de l'assistant (GED-2) : l'écran reste monté derrière.
  const [assistantOuvert, setAssistantOuvert] = useState(false);
  const [q, setQ] = useState('');
  /* ── LA DEMANDE S'EXAMINE CHAMP PAR CHAMP, D'EMBLÉE (RQ, 19/10/2026) ──
     « Quand j'ai une demande de document, afficher directement les champs
     saisis par le demandeur et me laisser les vérifier/modifier avant
     validation. Ensuite, juste deux boutons : refuser (et justifier) et
     approuver (tous les champs correctement remplis). »
     Les champs n'apparaissaient qu'après un clic sur « Approuver » ou
     « Approuver avec modif. » — deux portes vers le même formulaire, plus
     « À discuter » (le fil de discussion suffit). Chaque demande à statuer
     porte désormais SON brouillon, ouvert d'office et conservé tant que
     l'écran vit : changer d'onglet ou chercher ne perd rien.
     « Approuvée avec modifications » n'est plus un bouton : c'est un CONSTAT,
     posé quand une métadonnée diffère de la proposition du demandeur. */
  const [brouillons, setBrouillons] = useState<Record<string, AssignDraft>>({});
  // Motif d'un refus — obligatoire, saisi dans la carte (plus de `window.prompt`).
  const [motifDraft, setMotifDraft] = useState<{ id: string; texte: string; erreur: string } | null>(null);
  const { run: runDem, estOccupe: occupeDem } = useBusy();
  const { demander, confirmation } = useConfirmation();
  const ajouterNote = (id: string) => runDem(`note-${id}`, async () => {
    const t = (noteDrafts[id] || '').trim();
    if (!t) return;
    const ok = await addDemandeNote(id, t);
    if (ok) setNoteDrafts({ ...noteDrafts, [id]: '' });
  });
  const usersWithRole = (role: string) => {
    // Un compte suspendu ou archivé n'est plus proposé : lui confier la
    // rédaction d'un document neuf le laisserait sans acteur.
    const dispo = (settings?.gedUsers || []).filter(estAffectable);
    const list = dispo.filter((u) => (u.roles || []).includes(role));
    return list.length ? list : dispo;
  };
  const txt = (v: unknown) => (v === undefined || v === null ? '' : String(v));
  const brouillonInitial = (p: GedDemandeProps): AssignDraft => ({
      redacteurs: p.redacteur ? [p.redacteur] : [], verificateur: txt(p.verificateur),
      approbateur: txt(p.approbateur), periodicite: txt(p.periodicite) || 'biennale', motif: '',
      code: txt(p.code), titre: txt(p.titre), type: txt(p.type) || 'MO',
      processus: txt(p.processus), discipline: txt(p.discipline),
      site: txt(p.site),
      /* Périmètre = un SITE (RQ, 04/10/2026). Une demande déposée avant porte
         une phrase « Fonctions : … » : on propose alors le site de la demande,
         à défaut le site principal. Ses fonctions et postes cochés partent
         quand même au serveur, qui en fait l'audience par défaut. */
      perimetre: (settings?.sites || []).includes(txt(p.perimetre)) ? txt(p.perimetre)
        : ((settings?.sites || []).includes(txt(p.site)) ? txt(p.site) : (settings?.activeSite || (settings?.sites || [])[0] || '')),
      perimetrePostes: Array.isArray(p.perimetrePostes) ? p.perimetrePostes.map(String) : [],
      perimetreFonctions: Array.isArray(p.perimetreFonctions) ? p.perimetreFonctions.map(String) : [],
  });
  const brouillonDe = (id: string, p: GedDemandeProps) => brouillons[id] ?? brouillonInitial(p);
  const majBrouillon = (id: string, d: AssignDraft) => setBrouillons((b) => ({ ...b, [id]: d }));
  /** Un document externe n'a ni rédacteur ni approbateur — cf. le contrôleur. */
  const estExterneDraft = (d: AssignDraft | null) => (d?.type || '').toUpperCase() === 'DE';
  /* Ce qui manque pour approuver, nommé champ par champ. Discipline, site et
     vérificateur ont un « — aucun — » explicite : c'est un choix, pas un oubli.
     La référence est revérifiée contre le fonds ici, avant l'envoi (le serveur
     la refuse de toute façon, mais après un aller-retour). */
  const manquesDe = (d: AssignDraft): string[] => {
    const m: string[] = [];
    const code = d.code.trim();
    if (!code) m.push('Référence');
    else if (docParCode.has(code)) m.push(`Référence (${code} déjà attribuée)`);
    if (!d.titre.trim()) m.push('Titre');
    if (!d.type) m.push('Type');
    if (!d.processus) m.push('Processus');
    if ((settings?.sites || []).length && !d.perimetre) m.push('Périmètre');
    if (!estExterneDraft(d)) {
      if (!d.redacteurs.length) m.push('Rédacteur');
      if (!d.approbateur) m.push('Approbateur');
    }
    if (!d.periodicite) m.push('Fréquence de revue');
    return m;
  };
  const approuver = async (id: string, p: GedDemandeProps) => {
    const d = brouillonDe(id, p);
    if (manquesDe(d).length) return;
    const initial = brouillonInitial(p);
    const meta = {
      code: d.code.trim(), titre: d.titre.trim(), type: d.type,
      processus: d.processus, discipline: d.discipline,
      site: d.site, perimetre: d.perimetre,
      perimetrePostes: d.perimetrePostes,
      perimetreFonctions: d.perimetreFonctions,
    };
    const modifiee = (['code', 'titre', 'type', 'processus', 'discipline', 'site', 'perimetre'] as const)
      .some((k) => meta[k] !== initial[k].trim());
    const ok = await decideDemande(id, modifiee ? 'approuvee_mod' : 'approuvee', d.motif.trim(), {
      redacteurs: d.redacteurs, redacteur: d.redacteurs[0], verificateur: d.verificateur,
      approbateur: d.approbateur, periodicite: d.periodicite, meta,
    });
    if (ok) setBrouillons((b) => { const n = { ...b }; delete n[id]; return n; });
  };

  // ---- Répartition des demandes dans les quatre onglets -------------------
  // La suite donnée à une demande approuvée se lit sur le DOCUMENT qu'elle a
  // fait naître : on l'indexe une fois pour toutes.
  const docParCode = useMemo(() => new Map(docs.map((d) => [d.code, d])), [docs]);
  const ongletDe = React.useCallback((r: typeof demandes[number]): Onglet => {
    if (!r.decision) return 'statuer';
    if (r.decision === 'discuter') return 'discutees';
    if (r.decision === 'refusee') return 'archives';
    const code = r.propositions?.code;
    const issu = code ? docParCode.get(code) : undefined;
    return issu && EN_CIRCUIT.includes(issu.statut) ? 'enrevue' : 'archives';
  }, [docParCode]);

  const parOnglet = useMemo(() => {
    const acc: Record<Onglet, typeof demandes> = { statuer: [], discutees: [], enrevue: [], archives: [] };
    demandes.forEach((r) => acc[ongletDe(r)].push(r));
    return acc;
  }, [demandes, ongletDe]);

  const needle = q.toLowerCase().trim();
  const visibles = parOnglet[onglet].filter((r) => {
    if (!needle) return true;
    const p = r.propositions || ({} as typeof r.propositions);
    return `${p.code || ''} ${p.titre || ''} ${r.description || ''}`.toLowerCase().includes(needle);
  });

  /* ── LES RÈGLES DU CIRCUIT, JUGÉES PENDANT LA SAISIE (RQ, 19/10/2026) ──
     Rôle au paramétrage, séparation des tâches, habilitation sur le processus
     ou la discipline, compte actif : c'est le MOTEUR qui juge
     (`POST ged/demandes/:id/eligibilites`), avec la fonction même qui refusera
     l'approbation. L'écran ne recalcule rien ; il redemande à chaque
     changement d'un intervenant ou d'un axe, et n'ouvre l'approbation que sur
     un verdict À JOUR. */
  const [jugements, setJugements] = useState<Record<string, Jugement>>({});
  const aJuger = isRQ ? visibles.filter((r) => !r.decision || r.decision === 'discuter').map((r) => {
    const d = brouillonDe(r.documentId, r.propositions || ({} as GedDemandeProps));
    const corps = estExterneDraft(d) ? null : {
      redacteurs: d.redacteurs, verificateur: d.verificateur, approbateur: d.approbateur,
      meta: { processus: d.processus, discipline: d.discipline },
    };
    return { id: r.documentId, corps, cle: JSON.stringify(corps) };
  }) : [];
  const cleAJuger = JSON.stringify(aJuger.map((x) => [x.id, x.cle]));
  React.useEffect(() => {
    const restes = (JSON.parse(cleAJuger) as [string, string][])
      .filter(([id, cle]) => cle !== 'null' && jugements[id]?.cle !== cle);
    if (!restes.length) return undefined;
    let vivant = true;
    const minuterie = setTimeout(() => {
      for (const [id, cle] of restes) {
        void (async () => {
          let j: Jugement = { cle, etat: 'echec', erreurs: [], eligibles: null };
          try {
            const r = await fetchEcriture(`/api/ged/ged/demandes/${id}/eligibilites`, {
              method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: cle,
            });
            const d = await r.json().catch(() => null);
            if (r.ok && d?.eligibles) j = { cle, etat: 'ok', erreurs: d.erreurs || [], eligibles: d.eligibles };
          } catch { /* j reste en échec : l'écran le dit */ }
          if (vivant) setJugements((js) => ({ ...js, [id]: j }));
        })();
      }
    }, 250);
    return () => { vivant = false; clearTimeout(minuterie); };
    // `jugements` est relu à chaque passage ; la clé suffit à redéclencher.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleAJuger]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>  {/* `minmax(0, 1fr)` : un élément de grille refuse par défaut de
      descendre sous sa largeur de contenu, si bien qu'un registre large
      étirait le bandeau de titre et la barre de filtres hors du cadre
      (constaté sur les NC le 19/08/2026). Le `0` autorise la colonne à
      se réduire ; ce qui est large défile dans son propre conteneur. */}
      <TitrePage eyebrow="Avant la rédaction" titre="Demandes de nouveaux documents"
        sousTitre="La rédaction ne démarre qu’après validation du RQ — chaque demande est tracée et vérifiée contre les doublons du fonds documentaire."
        actions={
          /* Ce qui est tracé se VOIT (24/08/2026) : le recalcul des doublons
             s'écrivait sous « DEMANDES » sans accès à l'écran. */
          <BoutonJournal reference="DEMANDES" quoi="du registre des demandes" />
        } />

      {/* L'assistant n'est plus une colonne de cette page : c'est une fenêtre
          (GED-2). L'écran garde donc toute sa largeur pour la pile de travail. */}
      {assistantOuvert && <AssistantDemande onFerme={() => setAssistantOuvert(false)} />}
      {confirmation}

      <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Segment ariaLabel="État des demandes" valeur={onglet} onChange={setOnglet}
              options={ONGLETS.map(([cle, label, aide]) => ({ valeur: cle, libelle: `${label} (${parOnglet[cle].length})`, aide }))} />
            <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher (code, titre, description)…"
              style={{ height: 34, fontSize: 12.5, flex: 1, minWidth: 180, maxWidth: 'none' }} />
            {canWrite && (
              <BoutonCreer onClick={() => setAssistantOuvert(true)}>Nouvelle demande</BoutonCreer>
            )}
          </div>
          {visibles.length === 0 && (
            <div style={{ ...card, padding: '32px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5 }}>
              {q ? 'Aucune demande ne correspond à cette recherche.' : VIDE[onglet]}
            </div>
          )}
          {visibles.map((r) => {
            const p = r.propositions || ({} as typeof r.propositions);
            const dec = r.decision ? DECISIONS[r.decision] : null;
            const pending = !r.decision || r.decision === 'discuter';
            // Le RQ examine : les champs sont ouverts d'office, corrigeables.
            const examen = isRQ && pending;
            const d = examen ? brouillonDe(r.documentId, p) : null;
            const set = (patch: Partial<AssignDraft>) => d && majBrouillon(r.documentId, { ...d, ...patch });
            const manques = d ? manquesDe(d) : [];
            const externe = estExterneDraft(d);
            const cleCourante = aJuger.find((x) => x.id === r.documentId)?.cle;
            const jugement = jugements[r.documentId];
            const aJour = !!jugement && jugement.cle === cleCourante;
            const enControle = !!d && !externe && !aJour;
            const erreursRegles = d && !externe && aJour ? jugement.erreurs : [];
            const controleEchoue = !!d && !externe && aJour && jugement.etat === 'echec';
            const verdict = (role: RoleCircuit, id: string) => (aJour ? jugement.eligibles?.[role]?.find((x) => x.id === id) : undefined);
            const refuse = (role: RoleCircuit, id: string) => !!id && verdict(role, id)?.ok === false;
            const etiquette = (role: RoleCircuit, id: string, nom: string) => {
              const v = verdict(role, id);
              return v && !v.ok ? `${nom} — non éligible` : (v?.derogation ? `${nom} — par dérogation` : nom);
            };
            const manque = (champ: string) => manques.some((m) => m.startsWith(champ));
            const bord = (champ: string) => (manque(champ) ? 'var(--amber-600)' : undefined);
            return (
              <div key={r.documentId} style={{ ...card, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--navy-900)', color: 'var(--text-on-primary)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontFamily: 'var(--font-mono)' }}>{p.type}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{p.code}</span>
                  <span style={{ flex: 1 }} />
                  {dec
                    ? <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 10px', background: dec.bg, color: dec.fg }}>{dec.label}</span>
                    : <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 10px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>À statuer</span>}
                </div>
                <h3 style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)', margin: '10px 0 4px' }}>{p.titre}</h3>
                <p style={{ fontSize: 13, color: 'var(--text-body)', margin: 0, fontStyle: 'italic' }}>« {r.description} »</p>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
                  {examen
                    ? <span>Demandé par {userName(settings, r.demandeur)} le {fmtDate(r.createdAt)}</span>
                    : <>{p.discipline} · Rédacteur : {userName(settings, p.redacteur)} {p.verificateur && `· Vérif. : ${userName(settings, p.verificateur)}`} {p.approbateur && `· Approb. : ${userName(settings, p.approbateur)}`}
                  <span> · demandé par {userName(settings, r.demandeur)} le {fmtDate(r.createdAt)}</span></>}
                </div>
                <div style={{ fontSize: 12.5, marginTop: 6, color: (p.doublons || []).length ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                  Détection de doublons : {(p.doublons || []).length ? (
                    <>proche de {(p.doublons || []).map((code, i) => (
                      // Un doublon se CONSULTE pour trancher la demande : panneau,
                      // sinon on perdrait la demande en cours d'examen.
                      <button key={code} onClick={() => openDoc(code)} style={{ background: 'none', border: 'none', color: 'var(--color-primary-active)', fontWeight: 700, cursor: 'pointer', fontSize: 12.5, padding: 0, fontFamily: 'var(--font-mono)' }}>{i > 0 ? ', ' : ''}{code}</button>
                    ))}</>
                  ) : 'aucun doublon détecté.'}
                </div>
                {r.motif && <div style={{ fontSize: 12.5, marginTop: 8, padding: '8px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', color: 'var(--text-body)' }}>Décision : <strong>{dec?.label}</strong> — {r.motif}</div>}

                {/* Suite donnée à la demande : une demande approuvée n'est pas
                    une fin, c'est le début d'un circuit. On montre où en est le
                    document qui en est issu, cliquable pour y aller. */}
                {(() => {
                  const issu = p.code ? docParCode.get(p.code) : undefined;
                  if (!issu) {
                    // Tant qu'aucune décision n'est prise (ou qu'elle renvoie à
                    // discussion), il n'y a pas de « suite » à annoncer.
                    if (!r.decision || r.decision === 'discuter') return null;
                    return (
                      <div style={{ fontSize: 12.5, marginTop: 8, color: 'var(--text-muted)' }}>
                        Suite donnée : {r.decision === 'refusee' ? 'aucun document créé (demande refusée).' : 'document non retrouvé dans le fonds.'}
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 10, padding: '8px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)' }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Avancement :</span>
                      <StatePill statut={issu.statut} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>v{issu.versionCourante}</span>
                      {/* Le document issu de la demande est un objet LIÉ : on le
                          regarde par-dessus, la liste des demandes reste en place. */}
                      <button onClick={() => openDoc(issu.code)} style={{ background: 'none', border: 'none', color: 'var(--color-primary-active)', fontWeight: 700, cursor: 'pointer', fontSize: 12.5, padding: 0, fontFamily: 'var(--font-sans)' }}>
                        Ouvrir le document →
                      </button>
                    </div>
                  );
                })()}

                {/* Un refus n'est pas définitif : le RQ peut le rouvrir. Le
                    motif du refus est versé au fil, rien n'est effacé. */}
                {isRQ && r.decision === 'refusee' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <Bouton taille="sm" onClick={async () => { if (await demander('Annuler ce refus ? La demande repart « à statuer » et le motif du refus reste tracé au fil de discussion.', { confirmer: 'Annuler le refus' })) void reopenDemande(r.documentId); }}>
                      <I n="refresh" s={13} />Annuler le refus
                    </Bouton>
                  </div>
                )}

                {(r.notes || []).length > 0 && (
                  <div style={{ marginTop: 10, borderTop: '1px dashed var(--border-default)', paddingTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 5 }}>Fil de discussion — à cadrer avant décision</div>
                    {(r.notes || []).map((n) => (
                      <div key={n.id} style={{ fontSize: 12.5, padding: '3px 0', color: 'var(--text-body)' }}>
                        <strong style={{ color: 'var(--text-strong)' }}>{n.auteurName}</strong> · {fmtDate(n.ts)} — {n.texte}
                      </div>
                    ))}
                  </div>
                )}
                {pending && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <input value={noteDrafts[r.documentId] || ''} onChange={(e) => setNoteDrafts({ ...noteDrafts, [r.documentId]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        if (!(noteDrafts[r.documentId] || '').trim()) return;
                        void ajouterNote(r.documentId);
                      }}
                      placeholder="Ajouter une note au fil…" style={{ ...inputStyle, height: 34, fontSize: 12.5, flex: 1 }} />
                    <Bouton taille="sm" onClick={() => void ajouterNote(r.documentId)}
                      disabled={!(noteDrafts[r.documentId] || '').trim() || occupeDem()}
                      chargement={occupeDem(`note-${r.documentId}`)} libelleChargement="Ajout…">
                      Ajouter
                    </Bouton>
                  </div>
                )}

                {examen && d && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    {/* ── LA RÉFÉRENCE ET LES MÉTADONNÉES, CORRIGEABLES ICI ──
                        Demande de la RQ (14/08/2026) : « quand j'approuve une
                        demande avec modification, je veux modifier toutes les
                        métadonnées, ainsi que voir la référence du document ».
                        Le panneau ne portait que le circuit : le RQ voyait des
                        propositions qu'il ne pouvait pas corriger, et devait
                        demander au rédacteur de le faire après coup. */}
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)', marginBottom: 8 }}>
                      Saisie du demandeur — vérifiez et corrigez avant de statuer
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginBottom: 12 }}>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Référence</span>
                        <input value={d.code} onChange={(e) => set({ code: e.target.value })}
                          style={{ ...inputStyle, fontFamily: 'var(--font-mono)', borderColor: bord('Référence') }} />
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Titre</span>
                        <input value={d.titre} onChange={(e) => set({ titre: e.target.value })} style={{ ...inputStyle, borderColor: bord('Titre') }} />
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Type</span>
                        <select value={d.type} onChange={(e) => set({ type: e.target.value })} style={{ ...inputStyle, borderColor: bord('Type') }}>
                          {(settings?.docTypes || []).filter((t) => t.active !== false).map((t) => (
                            <option key={t.code} value={t.code}>{t.code} — {t.label}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Processus</span>
                        <Selecteur value={d.processus} onChange={(v) => set({ processus: v })} style={{ ...inputStyle, borderColor: bord('Processus') }}>
                          <option value="">—</option>
                          {(settings?.processus || []).filter((pp) => pp.active !== false).map((pp) => (
                            <option key={pp.id} value={pp.name}>{pp.name}</option>
                          ))}
                        </Selecteur>
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Discipline — famille biologique</span>
                        <Selecteur value={d.discipline} onChange={(v) => set({ discipline: v })} style={inputStyle}>
                          <option value="">— aucune —</option>
                          {(settings?.disciplines || []).filter((d) => d.active).map((d) => (
                            <option key={d.name} value={d.name}>{d.name}</option>
                          ))}
                        </Selecteur>
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Site</span>
                        {/* Liste fermée, alimentée par le paramétrage (onglet
                            « Sites du laboratoire »). Le rattachement reste
                            FACULTATIF — d'où « — aucun ».
                            La valeur déjà portée par la demande est conservée
                            en option même si elle ne figure plus au
                            paramétrage : un site retiré de la liste ne doit pas
                            être remplacé en silence par le premier venu au
                            simple fait d'ouvrir le formulaire. */}
                        <Selecteur value={d.site} onChange={(v) => set({ site: v })} style={inputStyle}>
                          <option value="">— aucun —</option>
                          {(settings?.sites || []).map((s) => <option key={s} value={s}>{s}</option>)}
                          {!!d.site && !(settings?.sites || []).includes(d.site) && (
                            <option value={d.site}>{d.site} (hors paramétrage)</option>
                          )}
                        </Selecteur>
                      </label>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block' }}>
                          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Périmètre — site où le document s’applique</span>
                          <Selecteur value={d.perimetre} onChange={(v) => set({ perimetre: v })} style={{ ...inputStyle, borderColor: bord('Périmètre') }}>
                            {(settings?.sites || []).map((s) => <option key={s} value={s}>{s}</option>)}
                          </Selecteur>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.45 }}>
                            Qui doit le lire se choisit à la diffusion. Par défaut : tout le personnel du site
                            {d.perimetreFonctions.length + d.perimetrePostes.length
                              ? ' — ici, les fonctions et postes proposés par la demande.' : '.'}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)', marginBottom: 8 }}>
                      {estExterneDraft(d)
                        ? 'Document externe — aucun intervenant à désigner'
                        : 'Affectation du circuit documentaire — obligatoire pour approuver'}
                    </div>
                    {estExterneDraft(d) && (
                      <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        Ce texte n’est pas rédigé par le laboratoire : il ne se rédige, ni ne se vérifie, ni ne
                        s’approuve. La fiche naîtra <strong>enregistrée</strong>, et c’est vous qui adopterez
                        l’édition une fois l’exemplaire déposé.
                      </p>
                    )}
                    <div style={{ display: estExterneDraft(d) ? 'none' : 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Rédacteur(s) * — coédition possible</span>
                        <div style={{ display: 'grid', gap: 4, padding: '8px 10px', border: `1px solid ${d.redacteurs.some((k) => refuse('redacteur', k)) ? 'var(--red-600)' : (d.redacteurs.length ? 'var(--border-default)' : 'var(--amber-600)')}`, borderRadius: 'var(--radius-sm)', maxHeight: 120, overflowY: 'auto' }}>
                          {usersWithRole('redacteur').map((u) => (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
                              <input type="checkbox" checked={d.redacteurs.includes(u.id)}
                                onChange={(e) => set({
                                  redacteurs: e.target.checked
                                    ? [...d.redacteurs, u.id]
                                    : d.redacteurs.filter((k) => k !== u.id),
                                })} />
                              {u.nom}
                              {d.redacteurs.includes(u.id) && refuse('redacteur', u.id) && (
                                <span style={{ fontSize: 11.5, color: 'var(--red-600)' }}>— {verdict('redacteur', u.id)?.raison}</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Vérificateur</span>
                        <Selecteur value={d.verificateur} onChange={(v) => set({ verificateur: v })} style={{ ...inputStyle, borderColor: refuse('verificateur', d.verificateur) ? 'var(--red-600)' : undefined }}>
                          <option value="">— aucun —</option>
                          {usersWithRole('verificateur').map((u) => <option key={u.id} value={u.id}>{etiquette('verificateur', u.id, u.nom)}</option>)}
                        </Selecteur>
                      </label>
                      <label style={{ display: 'block' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Approbateur *</span>
                        <Selecteur value={d.approbateur} onChange={(v) => set({ approbateur: v })} style={{ ...inputStyle, borderColor: refuse('approbateur', d.approbateur) ? 'var(--red-600)' : (d.approbateur ? undefined : 'var(--amber-600)') }}>
                          <option value="">— choisir —</option>
                          {usersWithRole('approbateur').map((u) => <option key={u.id} value={u.id}>{etiquette('approbateur', u.id, u.nom)}</option>)}
                        </Selecteur>
                      </label>
                    </div>
                    {erreursRegles.length > 0 && (
                      <div role="alert" style={{ marginTop: 10, padding: '8px 12px', border: '1px solid var(--red-600)', borderRadius: 'var(--radius-sm)', background: 'var(--red-50)' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)', marginBottom: 4 }}>
                          Intervenants refusés par les règles du circuit
                        </div>
                        {erreursRegles.map((e) => (
                          <div key={e} style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.45 }}>• {e}</div>
                        ))}
                      </div>
                    )}
                    {controleEchoue && (
                      <div role="status" style={{ marginTop: 10, fontSize: 12.5, color: 'var(--amber-700)' }}>
                        Le contrôle des règles du circuit n’a pas répondu : l’approbation le refera au moment de l’envoi.
                      </div>
                    )}
                    {/* La fréquence de revue vaut pour les DEUX parcours : une
                        norme externe se revoit elle aussi (une édition paraît).
                        Elle reste donc hors du bloc masqué. */}
                    <label style={{ display: 'block', maxWidth: 320 }}>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Fréquence de revue *</span>
                      <select value={d.periodicite} onChange={(e) => set({ periodicite: e.target.value })} style={inputStyle}>
                        {PERIODICITES.map((pp) => <option key={pp.key} value={pp.key}>{pp.label}</option>)}
                      </select>
                    </label>
                    <label style={{ display: 'block', marginTop: 10 }}>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Remarques au rédacteur (facultatif)</span>
                      <textarea value={d.motif} onChange={(e) => set({ motif: e.target.value })} rows={2} placeholder="Points à intégrer dans le brouillon…" style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
                    </label>

                    {motifDraft?.id === r.documentId ? (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                        <label htmlFor={`motif-${r.documentId}`} style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--red-600)', marginBottom: 6 }}>
                          Motif du refus <span style={{ color: 'var(--red-600)' }}>*</span>
                        </label>
                        <textarea id={`motif-${r.documentId}`} autoFocus rows={2} value={motifDraft.texte}
                          onChange={(e) => setMotifDraft({ ...motifDraft, texte: e.target.value, erreur: '' })}
                          aria-invalid={!!motifDraft.erreur}
                          placeholder="Ex. doublon avec MO-PRE-004 déjà en vigueur…"
                          style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical', borderColor: motifDraft.erreur ? 'var(--red-600)' : undefined }} />
                        <FieldError>{motifDraft.erreur}</FieldError>
                        <PiedActions
                          taille="sm"
                          style={{ marginTop: 8 }}
                          annuler={{ onClick: () => setMotifDraft(null) }}
                          principale={{
                            libelle: 'Confirmer le refus',
                            chargement: occupeDem(`refuser-${r.documentId}`), libelleChargement: 'Refus…',
                            disabled: occupeDem(),
                            onClick: () => void runDem(`refuser-${r.documentId}`, async () => {
                              const t = motifDraft.texte.trim();
                              if (!t) { setMotifDraft({ ...motifDraft, erreur: 'Un motif est obligatoire pour refuser une demande.' }); return; }
                              const ok = await decideDemande(r.documentId, 'refusee', t);
                              if (ok) setMotifDraft(null);
                            }),
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                        {enControle && manques.length === 0 && (
                          <div role="status" style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 8 }}>
                            Contrôle des intervenants en cours…
                          </div>
                        )}
                        {manques.length > 0 && (
                          <div role="status" style={{ fontSize: 12.5, color: 'var(--amber-700)', marginBottom: 8 }}>
                            Pour approuver, compléter : {manques.join(', ')}.
                          </div>
                        )}
                        {/* Deux décisions, pas plus (RQ, 19/10/2026). Un refus se rouvre
                            (« Annuler le refus ») et ne supprime rien : il n'est donc pas un
                            « danger » au sens des conventions. */}
                        <PiedActions
                          taille="sm"
                          secondaires={[
                            { libelle: 'Refuser', onClick: () => setMotifDraft({ id: r.documentId, texte: '', erreur: '' }) },
                          ]}
                          principale={{
                            /* DÉSACTIVÉ PENDANT L'ENVOI (14/10/2026) : un double clic
                               envoyait deux approbations, et chacune créait son
                               brouillon v0.1. Le serveur refuse désormais la seconde
                               (409), mais on n'envoie pas ce qu'on sait refusé. */
                            libelle: 'Approuver la demande', onClick: () => void runDem(`approuver-${r.documentId}`, () => approuver(r.documentId, p)),
                            chargement: occupeDem(`approuver-${r.documentId}`), libelleChargement: 'Approbation…',
                            disabled: occupeDem() || manques.length > 0 || erreursRegles.length > 0 || enControle,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
    </div>
  );
}
